/**
 * A FELHASZNÁLÓK SZELETÉNEK ÁLLAPOTA — a Beállítások lap második szelete (#25).
 *
 * A `useCompanies` mintájára: a `fail` ág, az `alive` őr és az `inFlight` zár
 * ugyanaz a három fogás, ugyanazokkal az okokkal. Egy harmadik horog-minta
 * itt nem nyerne semmit.
 *
 * ═══ HÁROM ELTÉRÉS, MIND SZÁNDÉKOS ═══
 *
 *   • A CÉGEK LISTÁJÁT IS BETÖLTI. A „mit lát" oszlopnak és a kiosztás-
 *     szerkesztőnek a cég NEVE kell, nem az azonosítója.
 *   • AZ ÍRÁS 403-A NEM TILTJA LE A LAPOT. A betöltés 403-a azt jelenti, hogy
 *     ide nincs jogod; az írásé azt, hogy EZT nem teheted meg („a saját
 *     fiókodat nem inaktiválhatod"). Az utóbbi a szerver mondatával jelenik
 *     meg, és a lista marad.
 *   • AZ ŰRLAP A SIKER VISSZAHÍVÁSÁRA ZÁRUL, nem egy `saved` állapotra. A siker
 *     után azonnal újratöltünk, és a két állapotírás egy renderelésbe esik: az
 *     űrlap a `saved`-et soha nem látná.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import { ApiError, api } from '@/lib/api-client';
import type { CompanyRow } from './useCompanies';

export type UserRole = 'ADMIN' | 'ACCOUNTANT' | 'CLIENT';
export type OfficeRole = Exclude<UserRole, 'CLIENT'>;
export type UserScope = 'ALL_COMPANIES' | 'ASSIGNED' | 'OWN_COMPANY';
export type AssignableScope = Exclude<UserScope, 'OWN_COMPANY'>;

/** Egy sor a nyilvántartásban — a backend `UserAdminRow`-ja. */
export interface UserRow {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  scope: UserScope;
  /** IDENTITÁS — csak ügyfél-felhasználónál van kitöltve. */
  companyId: string | null;
  /** JOGOSULTSÁG — csak `ASSIGNED` mellett van jelentése. */
  companyIds: string[];
  active: boolean;
  /** Beléphet-e ügyfélként (#76). Csak könyvelőnél állítható; az ADMIN mindig beléphet. */
  canImpersonate: boolean;
  /** `null`: még soha nem lépett be — irodai felhasználónál: a meghívó függőben. */
  lastLoginAt: string | null;
  createdAt: string;
}

export interface AccessInput {
  scope: AssignableScope;
  companyIds: string[];
}

export interface UserInput extends AccessInput {
  email: string;
  name: string;
  role: OfficeRole;
}

type InvitationOutcome = 'sent' | 'failed';

/** Egy meghívó sorsa — a lap tetején mondja ki, mi történt. */
export interface InvitationNotice {
  outcome: InvitationOutcome;
  email: string;
}

export type UsersStatus =
  | 'loading'
  | 'ready'
  | 'loadFailed'
  | 'forbidden'
  | 'saving'
  | 'saved'
  | 'failed'
  | 'invalid'
  | 'conflict'
  /** Írásra jött 403 — a szerver megmondja, miért nem. */
  | 'refused';

export interface UsersState {
  status: UsersStatus;
  users: UserRow[];
  companies: CompanyRow[];
  /** A szerver üzenete a 400-as, a 403-as és a 409-es ághoz. */
  detail: string;
  invitation: InvitationNotice | null;
  create: (input: UserInput, onSaved: () => void) => void;
  setAccess: (userId: string, input: AccessInput, onSaved: () => void) => void;
  setActive: (userId: string, active: boolean) => void;
  setImpersonation: (userId: string, canImpersonate: boolean) => void;
  resendInvitation: (user: UserRow) => void;
  dismissInvitation: () => void;
  /** A „Mégse" hívja — `useCompanies.clearWriteError`, ugyanazért. */
  clearWriteError: () => void;
  reload: () => void;
}

/** Az írás hibaágai — ezeket viszi el a `clearWriteError`. A `forbidden` NEM: az a lapé. */
const WRITE_ERRORS: ReadonlySet<UsersStatus> = new Set([
  'failed',
  'invalid',
  'conflict',
  'refused',
]);

/** Egy írás hibája → a lap állapota. `null`: lejárt munkamenet. */
function writeFailure(caught: unknown): { status: UsersStatus; message: string } | null {
  const httpStatus = caught instanceof ApiError ? caught.status : 0;
  const message = caught instanceof ApiError ? caught.message : '';

  switch (httpStatus) {
    case 401:
      return null;
    case 403:
      return { status: 'refused', message };
    case 400:
      return { status: 'invalid', message };
    case 409:
      // ⚠️ A SZERVER MONDATA, SZÓ SZERINT: „ezzel az e-mail címmel már van
      // felhasználó", „közben más is módosította" — a teendő mindkettőben más.
      return { status: 'conflict', message };
    default:
      return { status: 'failed', message };
  }
}

export function useUsers(): UsersState {
  const { signOut } = useAuth();
  const [status, setStatus] = useState<UsersStatus>('loading');
  const [users, setUsers] = useState<UserRow[]>([]);
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [detail, setDetail] = useState('');
  const [invitation, setInvitation] = useState<InvitationNotice | null>(null);

  /**
   * ⚠️ A ZÁR REFERENCIÁN ÜL, NEM ÁLLAPOTON — `useCompanies` fejléce. Itt egy
   * dupla felvétel a SAJÁT első mentésre adna „foglalt e-mail" 409-et, és a
   * kolléga két meghívót kapna.
   */
  const inFlight = useRef(false);

  /** A lecsatolt lap nem ír állapotot. */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /** ⚠️ CSAK A LEGUTÓBBI BETÖLTÉS ÍRHAT — `useCompanies` `latestLoad`, ugyanazért. */
  const latestLoad = useRef(0);

  const reload = useCallback(() => {
    latestLoad.current += 1;
    const load = latestLoad.current;
    setStatus('loading');

    void Promise.all([api<UserRow[]>('/users'), api<CompanyRow[]>('/companies')])
      .then(([userList, companyList]) => {
        if (!alive.current || load !== latestLoad.current) {
          return;
        }
        setUsers(userList);
        setCompanies(companyList);
        setStatus('ready');
      })
      .catch((caught: unknown) => {
        if (!alive.current || load !== latestLoad.current) {
          return;
        }
        const httpStatus = caught instanceof ApiError ? caught.status : 0;
        if (httpStatus === 401) {
          signOut('expired');
          return;
        }
        setStatus(httpStatus === 403 ? 'forbidden' : 'loadFailed');
      });
  }, [signOut]);

  useEffect(() => {
    reload();
  }, [reload]);

  /**
   * A NÉGY ÍRÁSI ÚT KÖZÖS TÖRZSE. A siker után újratöltünk, nem a helyi
   * tömböt írjuk át: a szerver számol (a kiosztás rendezése, a kisbetűsített
   * e-mail), és egy helyi összefésülés ebben csendben elcsúszna.
   */
  const write = useCallback(
    <T>(path: string, method: 'POST' | 'PUT', body: unknown, onSuccess?: (result: T) => void) => {
      if (inFlight.current) {
        return;
      }
      inFlight.current = true;
      setStatus('saving');
      setDetail('');

      void api<T>(path, {
        method,
        body: body === undefined ? undefined : JSON.stringify(body),
      })
        .then((result) => {
          if (!alive.current) {
            return;
          }
          onSuccess?.(result);
          setStatus('saved');
          reload();
        })
        .catch((caught: unknown) => {
          if (!alive.current) {
            return;
          }
          const failure = writeFailure(caught);
          if (failure === null) {
            signOut('expired');
            return;
          }
          setStatus(failure.status);
          setDetail(failure.message);
        })
        .finally(() => {
          inFlight.current = false;
        });
    },
    [reload, signOut]
  );

  const create = useCallback(
    (input: UserInput, onSaved: () => void) =>
      write<UserRow & { invitation: InvitationOutcome }>('/users', 'POST', input, (result) => {
        // ⚠️ A `failed` NEM A FELVÉTEL HIBÁJA: a felhasználó létrejött, csak a
        // levél nem ment ki. Az űrlap ezért ZÁRUL, és a lap teteje mondja meg,
        // hogy a sorában újra kell küldeni.
        setInvitation({ outcome: result.invitation, email: result.email });
        onSaved();
      }),
    [write]
  );

  const setAccess = useCallback(
    (userId: string, input: AccessInput, onSaved: () => void) =>
      write(`/users/${userId}/access`, 'PUT', input, () => onSaved()),
    [write]
  );

  /**
   * ⚠️ KÜLÖN ÚTVONAL, NEM A HOZZÁFÉRÉS RÉSZE — ugyanaz az ok, mint a cégnél:
   * egy DÖNTÉS nem eshet bele egy űrlap mentésébe.
   */
  const setActive = useCallback(
    (userId: string, active: boolean) => write(`/users/${userId}/active`, 'PUT', { active }),
    [write]
  );

  /**
   * BELÉPHET-E ÜGYFÉLKÉNT (#76) — szintén külön útvonal: a jog ADÁSA vagy
   * ELVÉTELE döntés, és a szerver `USER_ACCESS_CHANGED`-ként naplózza.
   */
  const setImpersonation = useCallback(
    (userId: string, canImpersonate: boolean) =>
      write(`/users/${userId}/impersonation`, 'PUT', { canImpersonate }),
    [write]
  );

  const resendInvitation = useCallback(
    (user: UserRow) =>
      write<{ invitation: InvitationOutcome }>(
        `/users/${user.id}/invitation`,
        'POST',
        undefined,
        (result) => setInvitation({ outcome: result.invitation, email: user.email })
      ),
    [write]
  );

  const dismissInvitation = useCallback(() => setInvitation(null), []);

  const clearWriteError = useCallback(() => {
    setStatus((current) => (WRITE_ERRORS.has(current) ? 'ready' : current));
    setDetail('');
  }, []);

  return {
    status,
    users,
    companies,
    detail,
    invitation,
    create,
    setAccess,
    setActive,
    setImpersonation,
    resendInvitation,
    dismissInvitation,
    clearWriteError,
    reload,
  };
}
